import { Atom, atom } from 'jotai'

import { alphamapClient } from 'src/clients'
import { AMOwnershipGraphVisualResponse } from 'src/common-services'
import { atomFamily, atomWithLocalStorage } from 'src/utils/jotai'
import { getNodesToExpand, nodeGroupsToGraph, sortGraphNodes, GraphVisual } from 'src/v2/components/graph-widget/utils'

type Prams = { id: string; type: 'parcel' | 'entity' }

export const initialGraph = atomFamily<Prams, Atom<Promise<GraphVisual>>>(({ id, type }) =>
  atom(async (get) => {
    const res = type === 'entity' ? await get(entityGraph(id)) : await get(parcelGraph(id))
    const rootNode = res.nodes.find((node) => node.isRoot)
    if (!rootNode) {
      return res
    }
    const sortedGraph = sortGraphNodes(res)
    sortedGraph.nodes = sortedGraph.nodes.map((node) => ({ ...node, layer: node.isRoot ? 0 : 1 }))

    if (res.nodes.length >= 10 && res.nodes.length <= 20) {
      return sortedGraph
    }

    // For cases where there are more than 20 nodes,
    // we simply take the first 20 and group the rest.
    if (sortedGraph.nodes.length > 20) {
      const nodesToGroup = sortedGraph.nodes.slice(20)
      const relationsToGroup = nodesToGroup.flatMap((node) =>
        sortedGraph.relations.filter((relation) => relation.from === node.id || relation.to === node.id)
      )
      sortedGraph.nodes = sortedGraph.nodes.slice(0, 20)
      sortedGraph.relations = sortedGraph.relations.filter(
        (relation) => relationsToGroup.findIndex((rel) => rel.from === relation.from && rel.to === relation.to) === -1
      )

      const nodeGroups = nodeGroupsToGraph(rootNode.id, { nodes: nodesToGroup, relations: relationsToGroup })
      sortedGraph.nodes = [...sortedGraph.nodes, ...nodeGroups.nodes]
      sortedGraph.relations = [...sortedGraph.relations, ...nodeGroups.relations]

      return sortedGraph
    }

    const nodesToExpand = getNodesToExpand(sortedGraph)

    if (nodesToExpand.length === 0) {
      return sortedGraph
    }

    const expandedNodes = await Promise.all(
      nodesToExpand.map((node) => {
        if (node.type === 'PARCEL') {
          return alphamapClient.ownership.getParcelGraphVisualDirect(node.id)
        }
        return alphamapClient.ownership.getEntityGraphVisualDirect(node.id)
      })
    )

    for (const { nodes, relations } of expandedNodes) {
      sortedGraph.nodes = [...sortedGraph.nodes, ...nodes.map((node) => ({ ...node, layer: 2 }))]
      sortedGraph.relations = [...sortedGraph.relations, ...relations]
    }

    return sortedGraph
  })
)

const entityGraph = atomFamily<string, Atom<Promise<AMOwnershipGraphVisualResponse>>>((id) =>
  atom(async () => {
    const res = await alphamapClient.ownership.getEntityGraphVisualDirect(id)
    return res
  })
)

const parcelGraph = atomFamily<string, Atom<Promise<AMOwnershipGraphVisualResponse>>>((id) =>
  atom(async () => {
    const res = await alphamapClient.ownership.getParcelGraphVisualDirect(id)
    return res
  })
)

export const showHint = atomWithLocalStorage('ownership.graph.show_hint', true)
